import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Eye, Download, ChevronRight, Truck, CheckCircle2, Star } from 'lucide-react';
import { downloadInvoice } from '../lib/invoiceUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function OrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, product: null, orderId: null });
  const [reviewedItems, setReviewedItems] = useState(new Set());

  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${API}/orders`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          const ordersList = Array.isArray(data) ? data : (data.orders || []);
          setOrders(ordersList);
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      if (user) {
        fetchOrders();
      } else {
        setLoading(false);
      }
    }
  }, [authLoading, user]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/60';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'shipped':
        return 'bg-primary/10 text-primary';
      case 'confirmed':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface rounded-xl w-1/3" />
          <div className="h-32 bg-surface rounded-2xl" />
          <div className="h-32 bg-surface rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8" data-testid="orders-page">
      <h1 className="font-heading text-2xl md:text-3xl text-secondary mb-6">My Orders</h1>

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-xl text-secondary mb-2">No Orders Yet</h2>
          <p className="text-muted-foreground font-body mb-6">
            You haven't placed any orders yet. Start shopping!
          </p>
          <Button asChild className="rounded-full bg-primary">
            <Link to="/">Start Shopping</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.order_id}
              className="border border-gray-100 rounded-2xl p-4 md:p-6 hover:shadow-soft transition-shadow"
              data-testid={`order-${order.order_id}`}
            >
              {/* Order Header */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-body font-semibold text-secondary">
                      Order #{order.order_id}
                    </p>
                    <Badge className={`${getStatusColor(order.status)} capitalize`}>
                      {order.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground font-body">
                    Placed on {formatDate(order.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-body font-semibold text-secondary text-lg">
                    {formatPrice(order.total)}
                  </p>
                  <p className="text-xs text-muted-foreground font-body">
                    {order.items?.length || 0} items
                  </p>
                </div>
              </div>

              {/* Order Items Preview */}
              <div className="flex flex-wrap gap-3 mb-4">
                {order.items?.slice(0, 4).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-2 pr-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                     <div>
                      <p className="text-sm font-body text-secondary line-clamp-1">{item.name}</p>
                      <p className="text-xs text-muted-foreground font-body">Qty: {item.quantity}</p>
                      
                      {order.status === 'delivered' && !reviewedItems.has(`${order.order_id}-${item.product_id}`) && (
                        <button 
                          onClick={() => setReviewModal({ open: true, product: { product_id: item.product_id, name: item.name }, orderId: order.order_id })}
                          className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-primary hover:text-primary/80 uppercase tracking-widest group"
                        >
                          <Star className="w-3 h-3 group-hover:fill-primary" />
                          Rate Product
                        </button>
                      )}
                      {reviewedItems.has(`${order.order_id}-${item.product_id}`) && (
                        <span className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-success uppercase tracking-widest">
                          <CheckCircle2 className="w-3 h-3" />
                          Review Submitted
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {order.items?.length > 4 && (
                  <div className="flex items-center justify-center w-12 h-12 bg-surface rounded-lg">
                    <span className="text-sm text-muted-foreground font-body">
                      +{order.items.length - 4}
                    </span>
                  </div>
                )}
              </div>

              {/* Order Actions */}
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/order/${order.order_id}`}
                  className="flex items-center gap-2 text-sm text-primary hover:underline font-body"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </Link>
                {order.status !== 'cancelled' && order.status !== 'pending' && order.status !== 'delivered' && (
                  <Link
                    to={`/track-order?orderId=${order.order_id}`}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline font-body border border-blue-100 bg-blue-50 px-3 py-1 rounded-full transition-colors hover:bg-blue-100"
                  >
                    <Truck className="w-4 h-4" />
                    Track Order
                  </Link>
                )}
                {order.status === 'delivered' && (
                  <button 
                    onClick={() => downloadInvoice(order)}
                    className="flex items-center gap-2 text-sm text-secondary hover:text-primary font-body"
                  >
                    <Download className="w-4 h-4" />
                    Download Invoice
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {reviewModal.open && (
        <ReviewModal
          isOpen={reviewModal.open}
          onClose={() => setReviewModal({ open: false, product: null, orderId: null })}
          onSuccess={() => {
            setReviewedItems(prev => new Set([...prev, `${reviewModal.orderId}-${reviewModal.product.product_id}`]));
            setReviewModal({ open: false, product: null, orderId: null });
          }}
          product={reviewModal.product}
          orderId={reviewModal.orderId}
        />
      )}
    </div>
  );
}
